"use client";

import { ChartOptions } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { IRiskLevelProposal } from "@portal/types/robot/IRiskLevelProposal";
import { chartColors, chartJsTextColor } from "@portal/helpers/chartJs";

interface Props {
  riskLevel: IRiskLevelProposal | undefined;
  isDefaultLanguage: boolean;
}

/**
 * Chart.js version of the risk level donut chart.
 * @param interface
 * @returns a chart.
 */
export const RiskLevelChart = ({ riskLevel, isDefaultLanguage }: Props) => {
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const shares = riskLevel?.shares ?? 0;
  const interest = riskLevel?.interest ?? 0;

  const labels = [texts.SHARES, texts.INTEREST];
  const values = [shares, interest];
  const colors = [chartColors.blueHighcharts, chartColors.yellow];

  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "40%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: chartJsTextColor(),
          usePointStyle: true,
          pointStyle: "circle",
          generateLabels: chart => {
            const data = chart.data.datasets[0].data as number[];
            return (chart.data.labels as string[]).map((label, i) => ({
              text: `${data[i]}% ${label}`,
              fillStyle: colors[i],
              strokeStyle: colors[i],
              fontColor: chartJsTextColor(),
              pointStyle: "circle",
              index: i,
            }));
          },
        },
      },
      tooltip: {
        callbacks: {
          label: item => {
            const total = values.reduce((sum, v) => sum + v, 0);
            const pct = total ? ((item.raw as number) / total) * 100 : 0;
            return `${pct.toFixed(1)}%`;
          },
        },
      },
      datalabels: { display: false },
    },
  };

  return (
    <div style={{ height: 360 }}>
      <Doughnut data={{ labels, datasets: [{ data: values, backgroundColor: colors, borderWidth: 0 }] }} options={options} />
    </div>
  );
};
